import { lazy } from "react";
import * as XLSX from "xlsx";

export const sections = [
  {
    id: "beneficiaries",
    title: "Projeto e Beneficiário(s)",
    component: lazy(() => import("./Beneficiaries")),
  },
  {
    id: "economic",
    title: "Unidade Económica",
    component: lazy(() => import("./EconomicUnit")),
  },
  {
    id: "marketAnalysis",
    title: "Análise Mercado",
    component: lazy(() => import("./MarketAnalysis")),
  },
  {
    id: "project",
    title: "Dados do Projeto",
    component: lazy(() => import("./ProjectData")),
  },
  {
    id: "operation",
    title: "Caracterização da Operação",
    component: lazy(() => import("./OperationDescription")),
  },
  {
    id: "typologies",
    title: "Tipologias",
    component: lazy(() => import("./Typologies")),
  },
  {
    id: "activities",
    title: "Lista de Atividades",
    component: lazy(() => import("./Activities")),
  },
  {
    id: "travels",
    title: "Deslocações",
    component: lazy(() => import("./Travel")),
  },
  {
    id: "technicians",
    title: "Pessoal técnico",
    component: lazy(() => import("./Technicians")),
  },
  {
    id: "team",
    title: "Equipa",
    component: lazy(() => import("./Team")),
  },
  {
    id: "costs",
    title: "Custos",
    component: lazy(() => import("./Costs")),
  },
  {
    id: "amortization",
    title: "Amortização",
    component: lazy(() => import("./Amortization")),
  },
  {
    id: "industry40",
    title: "Industria 4.0",
    component: lazy(() => import("./Industry40")),
  },
  {
    id: "climateTransaction",
    title: "Transição Climática",
    component: lazy(() => import("./ClimateTransition")),
  },
  {
    id: "financing",
    title: "Financiamento",
    component: lazy(() => import("./Financing")),
  },
  {
    id: "thematic",
    title: "Enquadramento Temático",
    component: lazy(() => import("./Thematic")),
  },
  {
    id: "additions",
    title: "Majorações",
    component: lazy(() => import("./Additions")),
  },
  {
    id: "selectionCriteria",
    title: "Critérios de Seleção",
    component: lazy(() => import("./SelectionCriteria")),
  },
  {
    id: "declarations",
    title: "Declarações",
    component: lazy(() => import("./Declarations")),
  },
];

export const importFiles = {
  Despesas: (workbook, formData, jsonData, handleChange, setToast) => {
    const financingData = {};
    const costsData = [];
    const techData = [];
    const teamData = [];

    const contractMap = {
      "A contratar": "17",
      "Quadro - Contrato de Trabalho em Funções Públicas": "4",
      "Quadro - Contrato Tempo Indeterminado": "5",
      "Quadro - Contrato Termo Certo": "6",
      "Quadro - Contrato Termo Incerto": "7",
      Bolseiro: "18",
      "Quadro cedência ocasional": "19",
    };

    const ganttSheet = workbook.Sheets["Gantt"];

    const ganttMain = XLSX.utils.sheet_to_json(ganttSheet, {
      range: "C8:EJ63",
      header: ["trl", "id", "designation"],
      blankrows: true,
      skipHidden: false,
    });
    const ganttExtra = XLSX.utils.sheet_to_json(ganttSheet, {
      range: "BI8:BK63",
      header: ["startDate", "endDate", "innovationType"],
      blankrows: true,
      skipHidden: false,
    });

    const ganttRows = ganttMain.map((row, idx) => ({
      ...row,
      ...(ganttExtra[idx] || {}),
    }));

    const ganttData = ganttRows
      .filter((row) => row.id)
      .map((row) => {
        const { trl, id, designation, startDate, endDate, innovationType } =
          row;

        const typology = trl === "Inovação" ? "INOV" : "IDT";
        const activityDegree =
          trl === "[3-4]" ? "01" : trl === "[5-9]" ? "02" : "";

        return {
          id,
          typology,
          startDate:
            excelSerialToJSDate(startDate)?.toISOString().split("T")[0] ?? "",
          endDate:
            excelSerialToJSDate(endDate)?.toISOString().split("T")[0] ?? "",
          designation,
          activityDegree,
          innovationType,
        };
      });

    let techId = 1;
    // Team & FTES
    const conditionsSheet = workbook.Sheets["Condições Validação I&D"];
    XLSX.utils
      .sheet_to_json(conditionsSheet, {
        range: "O10:AE10",
        header: 1,
        skipHidden: false,
      })[0]
      .forEach((company, ind) => {
        const ben = formData["beneficiaries"]?.find(
          (b) => b.name?.trim() === company?.trim()
        );

        if (!ben) return;

        const firstCol = 10 + (ind == 0 ? 0 : 1);
        const blockWidth = 6;
        const numBlocks = 25;
        const dataRowStart = 12;
        const dataRowEnd = 67;

        const mainLocation = formData["projectLocation"]?.find(
          (loc) => loc.beneficiary == ben.id
        )?.id;

        const teamSheet =
          ind === 0
            ? workbook.Sheets[`CPessoal Líder`]
            : workbook.Sheets[`CPessoal Co${ind + 1}`];

        for (let i = 0; i < numBlocks; i++) {
          const startColNum = firstCol + i * blockWidth;
          const endColNum = startColNum + blockWidth - 1;
          const startColLetter = colLetter(startColNum);
          const endColLetter = colLetter(endColNum);

          const wage = teamSheet[`${colLetter(startColNum + 2)}9`]?.v;
          if (!wage) continue;

          const bond = teamSheet[`${colLetter(startColNum + 1)}7`]?.v;
          const isNew = bond === "A contratar" ? "S" : "N";
          const name = teamSheet[`${colLetter(startColNum + 1)}8`]?.v;
          const level = `Q${
            teamSheet[`${colLetter(startColNum + 3)}7`]?.v?.split(" ")[1]
          }`;
          const contractBond = contractMap[bond] || "";
          const tsu =
            contractBond === "18" // Bolseiro
              ? ""
              : teamSheet[`${colLetter(startColNum + 5)}9`]?.v * 100 ?? "";
          const critical =
            teamSheet[`${colLetter(startColNum + 4)}7`]?.v === "Sim" ? "S" : "";

          const range = `${startColLetter}${dataRowStart}:${endColLetter}${dataRowEnd}`;
          XLSX.utils
            .sheet_to_json(teamSheet, {
              range,
              header: ["content", "2025", "2026", "2027", "2028", "2029"],
              blankrows: true,
              skipHidden: false,
            })
            .forEach((row, ind) => {
              if (
                !Object.keys(row).some(
                  (key) =>
                    ["2025", "2026", "2027", "2028", "2029"].includes(key) &&
                    row[key]
                )
              ) {
                return;
              }

              const activityId = ganttData.find(
                (activity) =>
                  activity.designation?.trim() ===
                  teamSheet[`B${dataRowStart + ind}`]?.v?.trim()
              )?.id;

              if (!activityId) return;

              const locationDescription =
                teamSheet[`${colLetter(startColNum + 5)}7`]?.v;

              const location = locationDescription
                ? formData["projectLocation"]?.find(
                    (loc) =>
                      loc.beneficiary == ben.id &&
                      loc.description === locationDescription
                  )?.id
                : mainLocation;

              const totalEti = [
                "2025",
                "2026",
                "2027",
                "2028",
                "2029",
                "2030",
              ].reduce((acc, year) => {
                if (!row[year]) return acc;

                return acc + parseFloat(row[year]);
              }, 0);

              const totalCost =
                contractBond === "18" // Bolseiro
                  ? parseFloat(wage) * totalEti
                  : // For other contracts, apply the full calculation
                    parseFloat(wage) *
                    (14 / 12) *
                    (1 + parseFloat(tsu) / 100) *
                    totalEti *
                    (12 / 11);

              teamData.push({
                ...row,
                component: `IDT`,
                activity: String(activityId),
                location: String(location),
                technician: String(techId),
                totalCost,
              });
            });

          techData.push({
            id: String(techId),
            beneficiary: ben.id,
            new: isNew,
            name,
            level,
            contractBond,
            wage,
            tsu,
            critical,
          });

          techId = techId + 1;
        }
      });

    // Financing
    formData["beneficiaries"]?.forEach((ben, ind) => {
      const { nature, id, name } = ben;

      const initialLine = 15 + ind * 23;
      const finalLine = initialLine + 17;

      const financeSheet = workbook.Sheets[`Financiamento`];
      financingData[id] = XLSX.utils.sheet_to_json(financeSheet, {
        range: `G${initialLine}:G${finalLine}`,
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: ["value"],
      });
    });

    let costId = 1;
    const costsSheet = workbook.Sheets["Quadro Invest."];
    const sheetRange = XLSX.utils.decode_range(costsSheet["!ref"]);
    const lastRow = sheetRange.e.r + 1;
    XLSX.utils
      .sheet_to_json(costsSheet, {
        range: `A5:L${lastRow}`,
        header: [
          "location",
          "entity",
          "typology",
          "designation",
          "quantity",
          "costUnit",
          "investment",
          "aqMonth",
          "aqYear",
          "activity",
          "classification",
          "amount",
        ],
        blankrows: true,
        skipHidden: false,
      })
      .forEach((cost) => {
        const designation = cost.designation;

        if (!designation || designation === "-") return;

        const classification = cost.classification;
        if (
          classification === "Estadias (Internacionais)" ||
          classification === "Deslocações (Internacionais)"
        )
          return;

        const beneficiary = formData["beneficiaries"]?.find(
          (ben) => ben.name === cost.entity
        )?.id;

        if (!beneficiary) return;

        const location = formData["projectLocation"]?.find(
          (loc) =>
            loc.beneficiary == beneficiary &&
            (!cost.location || loc.description == cost.location)
        )?.id;

        const component = cost.typology === "IDT" ? "IDT" : "INOV";

        const activity = ganttData.find(
          (activity) => activity.designation?.trim() === cost.activity?.trim()
        )?.id;

        const rubric = jsonData["CostsRubric"]?.[component]?.find(
          (rubric) => rubric.label === classification
        )?.value;

        const month = excelSerialToJSDate(cost.aqMonth)?.getMonth() + 1;

        costsData.push({
          id: costId++,
          beneficiary,
          location,
          component,
          activity,
          designation,
          rubric,
          year: String(cost.aqYear),
          month: String(month),
          cost: cost.amount,
        });
      });

    handleChange("projectActivities", ganttData);
    handleChange("technicians", techData);
    handleChange("team", teamData);
    handleChange("financing", financingData);
    handleChange("projectCosts", costsData);

    setToast({
      title: "Despesas",
      message:
        "Atividades, Equipas, Custos e Financiamento foram carregadas com sucesso!",
      type: "success",
    });
  },
  Mérito: (workbook, formData, jsonData, handleChange, setToast) => {
    const meritoSheet = workbook.Sheets["Mérito Inov"];
    const activityPerMarketData = XLSX.utils
      .sheet_to_json(meritoSheet, {
        range: "L38:R82",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "service",
          "new",
          "country",
          "preQuantity",
          "preValue",
          "postQuantity",
          "postValue",
        ],
      })
      .map((act) => {
        if (!act.country) return;

        const service = jsonData["Services"]?.find(
          (service) => service.label === act.service
        )?.value;
        const country = jsonData["Countries"]?.find(
          (country) => country.label === act.country
        )?.value;
        const new_ = act.new === "Existente" ? "E" : "N";

        return { ...act, type: "01", service, new: new_, country };
      })
      .filter(Boolean);

    const leaderId = formData["beneficiaries"]?.[0]?.id;
    const finalData = {
      ...formData["activityPerMarket"],
      [leaderId]: activityPerMarketData,
    };

    handleChange("activityPerMarket", finalData);

    setToast({
      title: "Mérito",
      message: "Atividade Ecónomica do lider foi carregada com sucesso!",
      type: "success",
    });
  },
};

function colLetter(colNum) {
  let temp,
    letter = "";
  while (colNum > 0) {
    temp = (colNum - 1) % 26;
    letter = String.fromCharCode(temp + 65) + letter;
    colNum = (colNum - temp - 1) / 26;
  }
  return letter;
}

function excelSerialToJSDate(xlSerial) {
  const out = new Date(
    -2209075200000 + (xlSerial - (xlSerial < 61 ? 0 : 1)) * 86400000
  );
  if (isNaN(out.getTime())) {
    return null; // Invalid date
  }
  return out;
}
